// utils/preferences.ts
// Theme and language chosen by a visitor who is not logged in, kept in this browser.
// Logged-in users keep theirs in the profile (theme / preferredLanguage).

export type ThemePreference = 'dark' | 'light';
export type LocalePreference = 'pt' | 'en';

const THEME_KEY = 'sr-theme';
const LOCALE_KEY = 'sr-locale';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked (private mode, disabled cookies): the choice lasts only for this page view
  }
}

export function getStoredTheme(): ThemePreference | null {
  const value = read(THEME_KEY);
  return value === 'dark' || value === 'light' ? value : null;
}

export function getStoredLocale(): LocalePreference | null {
  const value = read(LOCALE_KEY);
  return value === 'pt' || value === 'en' ? value : null;
}

export function storeTheme(theme: ThemePreference): void {
  write(THEME_KEY, theme);
}

export function storeLocale(locale: LocalePreference): void {
  write(LOCALE_KEY, locale);
}

/** Stored choice, or the operating system's color scheme */
export function resolveIsDark(): boolean {
  const stored = getStoredTheme();
  if (stored) return stored === 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Stored choice, or the browser language */
export function resolveLocale(): LocalePreference {
  const stored = getStoredLocale();
  if (stored) return stored;
  const browserLang = navigator.language || (navigator as any).userLanguage || '';
  return browserLang.toLowerCase().startsWith('pt') ? 'pt' : 'en';
}
